import React from 'react';

import { useCameraStreamStart } from 'src/features/camera/api/useCameraStreamStart';
import {
    attachStreamToVideo,
    detachStreamFromVideo,
    ICE_SERVERS,
    STREAM_RESTART_DELAY_MS,
    waitForIceGatheringComplete,
} from 'src/features/videoPlayer/utils/webrtc';
import { useParking } from 'src/providers/ParkingProvider';

type Props = {
    startStream: boolean;
    selectedGateway: string;
    setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setError: React.Dispatch<React.SetStateAction<boolean>>;
    streamRef: React.RefObject<MediaStream | null>;
    videoRefs: React.RefObject<HTMLVideoElement | null>[];
};

export default function WebRTCVideoPlayer(props: Props) {
    const { selectedGateway, setIsPlaying, setLoading, setError, videoRefs, startStream, streamRef } = props;
    const { parkingId } = useParking();

    const { mutateAsync: startCameraWebRTC } = useCameraStreamStart({
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            setError(true);
        },
    });

    React.useEffect(() => {
        if (!startStream) return;
        // Guards every async continuation below: the effect can be torn down mid-handshake.
        let stopped = false;
        let connection: RTCPeerConnection | null = null;
        let restartTimer: ReturnType<typeof setTimeout> | undefined;

        const attachStream = (webrtc: RTCPeerConnection) => {
            videoRefs.forEach((videoRef) => {
                attachStreamToVideo(videoRef.current, streamRef.current)
                    ?.then(() => {
                        // Only report "playing" once a frame is really decoding: flipping these on
                        // `ontrack` uncovers the <video> while it is still black.
                        if (stopped || webrtc !== connection) return;

                        setLoading(false);
                        setIsPlaying(true);
                    })
                    .catch((error) => {
                        console.warn('play error', error);
                    });
            });
        };

        const detachVideo = () => {
            videoRefs.forEach((videoRef) => detachStreamFromVideo(videoRef.current));

            // Stop the tracks explicitly rather than relying on `pc.close()`: a <video> that was
            // unmounted before teardown still holds this stream and is out of our reach.
            streamRef.current?.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        };

        const teardown = () => {
            if (restartTimer) clearTimeout(restartTimer);
            restartTimer = undefined;

            connection?.close();
            connection = null;
            detachVideo();
        };

        /** Rebuild the peer connection after it drops, rather than leaving a frozen <video>. */
        const restart = () => {
            if (stopped) return;

            teardown();
            setIsPlaying(false);
            setLoading(true);
            restartTimer = setTimeout(start, STREAM_RESTART_DELAY_MS);
        };

        const negotiate = async (webrtc: RTCPeerConnection) => {
            // We only consume the camera; go2rtc has nothing to receive from us.
            webrtc.addTransceiver('video', { direction: 'recvonly' });

            await webrtc.setLocalDescription(await webrtc.createOffer());
            await waitForIceGatheringComplete(webrtc);
            if (stopped || webrtc !== connection) return;

            const response = await startCameraWebRTC({
                gatewayId: selectedGateway,
                parkingId,
                sdp: webrtc.localDescription?.sdp ?? '',
            });
            if (stopped || webrtc !== connection) return;

            await webrtc.setRemoteDescription({ type: 'answer', sdp: response.sdp });
        };

        function start() {
            if (stopped) return;

            const webrtc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
            connection = webrtc;

            webrtc.ontrack = (event) => {
                if (webrtc !== connection) return;

                // go2rtc does not always advertise a stream id, so fall back to the bare track.
                streamRef.current = event.streams[0] ?? new MediaStream([event.track]);

                attachStream(webrtc);
            };

            // Connection state replaces the keepalive data channel the previous streaming server
            // needed: go2rtc reports liveness through the peer connection itself.
            webrtc.onconnectionstatechange = () => {
                if (webrtc !== connection) return;

                // `connected` is deliberately not treated as "playing": the transport being up says
                // nothing about frames arriving. Only a resolved `play()` clears the skeleton.
                if (webrtc.connectionState === 'failed' || webrtc.connectionState === 'disconnected') {
                    restart();
                }
            };

            negotiate(webrtc).catch(() => {
                // A superseded connection failing must not poison the one that replaced it.
                if (stopped || webrtc !== connection) return;

                setLoading(false);
                setError(true);
            });
        }

        start();

        return () => {
            stopped = true;
            teardown();
            setIsPlaying(false);
            setLoading(true);
        };
        // `videoRefs` must be a stable array from the caller: a fresh literal on every render would
        // tear the peer connection down and renegotiate on every WS-driven re-render of the list.
    }, [
        selectedGateway,
        startCameraWebRTC,
        setIsPlaying,
        setLoading,
        setError,
        startStream,
        parkingId,
        streamRef,
        videoRefs,
    ]);

    return null;
}
